import { useTranslation } from 'react-i18next'
import { cn } from '@/shared/utils/cn'
import type { ReleaseProductOption } from './releases'

interface ReleaseFilterBubblesProps {
  /**
   * Solo aplicaciones CON notas; el orden lo decide el servidor. Ya vienen
   * plegadas por producto (goal 7), así que un `repo` puede ser un CSV de
   * varios: aquí es una cadena opaca que se emite tal cual.
   */
  options: ReleaseProductOption[]
  /** `null` = «Todas». */
  selected: string | null
  onSelect: (repo: string | null) => void
  className?: string
}

/**
 * Fila de burbujas para acotar el histórico a una aplicación (goal
 * portal-correcciones-6). Presentacional puro: recibe opciones, selección y
 * `onSelect` — quien decide qué se pide y qué va a la URL es la página.
 *
 * El valor que viaja es el `repo` (slug estable); la etiqueta visible es el
 * `product`. Pulsar la burbuja ACTIVA vuelve a «Todas», así que se puede
 * deshacer el filtro sin buscar el botón de quitar.
 */
export function ReleaseFilterBubbles({
  options,
  selected,
  onSelect,
  className,
}: ReleaseFilterBubblesProps) {
  const { t } = useTranslation()
  if (options.length === 0) return null

  const bubbles: Array<{ repo: string | null; label: string }> = [
    { repo: null, label: t('releases.filterAll') },
    ...options.map((option) => ({ repo: option.repo, label: option.product })),
  ]

  return (
    <div
      role="group"
      aria-label={t('releases.filterAria')}
      className={cn('flex flex-wrap gap-2', className)}
    >
      {bubbles.map(({ repo, label }) => {
        const active = selected === repo
        return (
          <button
            key={repo ?? '__todas__'}
            type="button"
            aria-pressed={active}
            onClick={() => onSelect(active ? null : repo)}
            className={cn(
              'rounded-full border px-4 py-1.5 text-xs font-semibold transition-colors',
              'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2',
              active
                ? 'border-primary bg-primary text-white'
                : 'border-black/10 bg-white text-muted-foreground hover:border-primary/40 hover:text-primary',
            )}
          >
            {label}
          </button>
        )
      })}
    </div>
  )
}
